<template>
  <header class="parts-details-header">
    <div class="product-summary hide-desktop">
      <h1 class="mobile-title">
        <small>Model No. Details for</small>
        <span
          ref="partIdLineMobile" class="part-id-line" :style="partIdMobileStyle"
          v-html="shownPartId"></span>
      </h1>
      <span class="sub-head">
        Load Capacity (lbs.): <small>{{ productDetails.LoadCapacity }}</small>
      </span>
      <div class="sub-head">
        Availability: <div v-html="productDetails.Availability" class="small"></div>
      </div>
    </div>
    <product-image
      ref="productImage"
      class="col-lg-6"
      :product-details="productDetails"
      :part-id="shownPartId"
      :is-valid-cad-user="isValidCadUser"
      :cad-user-email="cadUser ? cadUser.Email : null"
      :cad-user-full-name="cadUser ? cadUser.FullName : null"
      :cad-user-company="cadUser ? cadUser.Company : null"
      @display-cad-modal="$emit('display-cad-modal', true)" />
    <div class="product-summary col-lg-6">
      <h1 class="hide-mobile">
        <small>Model No. Details for</small>
        <span
          ref="partIdLineDesktop" class="part-id-line" :style="partIdDesktopStyle"
          v-html="shownPartId"></span>
      </h1>
      <span class="sub-head hide-mobile">
        Load Capacity (lbs.): <small>{{ productDetails.LoadCapacity }}</small>
      </span>
      <div class="sub-head hide-mobile">
        Availability: <div v-html="productDetails.Availability" class="small"></div>
      </div>
      <p class="product-description">
        <span v-html="productDetails.Description"></span>
      </p>

      <div v-if="optionsEverResolved" class="options-resolved-block">

      <section class="container-fluid part-options-section">
        <part-options-new
          v-if="isOptionRenderReady && !productDetails.IsObsoletePart"
          :items="normalizedItems"
          :combos="optionCombosList"
          :base-part-id="resolvedBasePartId"
          :option-types="effectiveOptionTypes"
          :selected-attributes="selectedAttributes"
          @part-id-changed="onPartIdChanged" />
      </section>

      <div class="purchase-row">
        <div v-if="!productDetails.IsObsoletePart" class="project-inline">
          <md-field>
            <label for="downloadProfileName">Quantity</label>
            <md-input
              id="downloadProfileName"
              type="number"
              v-model="productQuantity" />
          </md-field>
          <div class="button-wrap">
            <md-button 
              class="md-primary" 
              :disabled="!canAddToProject"
              @click="addToProject">
              <font-awesome-icon :icon="['fas', 'folder-open']" />
              Add to Project
            </md-button>
          </div>
        </div>
        <div class="price">
          <span class="price-label">{{ productDetails.UnitPriceLabel }}</span>
          <span class="price-value">
            <template v-if="isPriceAvailable">
              {{ formattedPrice }}
            </template>
            <template v-else>
              <a
                class="zero-price-link"
                :href="contactUrl"
                target="_blank"
                rel="noopener"
              >{{ zeroDollarMessage }}</a>
            </template>
          </span>
          <span class="caption-wrap">
            <span class="price-caption" v-if="isPriceAvailable">{{ productDetails.PricingCaption }}</span>
            <md-tooltip
              v-if="productDetails.PricingMessage != ''"
              class="info-popopver"
              md-direction="top">
              <div class="inner-popover">
                <span
                  class="popover-description"
                  v-html="productDetails.PricingMessage"></span>
              </div>
            </md-tooltip>
          </span>
        </div>
      </div>
      <div class="finder-button">
        <md-button
          class="md-outline md-theme-default product-search-button"
          :class="{'disabled': isSearchDisabled}"
          :disabled="isSearchDisabled"
          v-if="hasProductConfigAttributes"
          @click="onSearchParts">
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
          <span v-if="productType === ProductTypes.getKey(ProductTypes.Casters)">
            Find Similar Casters
          </span>
          <span v-else>
            Find Casters using this Wheel
          </span>
        </md-button>
      </div>

      </div>
    </div>
  </header>
</template>

<script>
import ProductImage from './ProductImage'
import PartOptionsNew from './PartOptionsNew'
import { addToCart } from '../../api'
import {CADDrawingTypes, ConfiguratorResultsPage, DownloadFormats, ProductTypes, ScrollToResults} from '../enums'

export default {
  name: 'product-header',
  components: {
    ProductImage,
    PartOptionsNew
  },
  props: {
    cadUser: {
      type: Object
    },
    productDetails: {
      type: Object,
      required: true
    },
    partsList: {
      type: Array
    },
    hasProductConfigAttributes: {
      type: Boolean,
      default: false
    },
    isValidCadUser: {
      type: Boolean,
      default: false
    },
    productType: {
      type: String
    },
    isSearchDisabled: {
      type: Boolean,
      default: true
    },
    selectedAttributes: {
      type: Array,
      default: () => []
    },
    // Product-configurator attributes (from ProductSpecifications) used by the finder
    searchAttributes: {
      type: Array,
      default: () => []
    },
    partId: {
      type: String,
      default: ''
    },
    preloadedOptionCombos: {
      type: [Object, Array, null],
      default: null
    },
    preloadedOptionTypes: {
      type: Array,
      default: () => []
    },
    optionsLoadComplete: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      emailLink: 'mailto:cad@hamiltoncaster.com?Subject=Request CAD for Part ID ',
      productQuantity: 1,
      CADDrawingTypes,
      selectedDownloadFormat: null,
      desiredFormatOptions: DownloadFormats,
      ProductTypes,
      cartId: null,
      optionCombosLoaded: false,
      optionCombos: null,
      optionTypes: [],
      optionTypesLoaded: false,
      // Removed overridePartId/overridePrice/hasOptionsSelected
      partIdDesktopSizeRem: null,
      partIdMobileSizeRem: null,
      partIdResizeObserver: null,
      partIdFitTimer: null,
      optionsEverResolved: false
    }
  },
  computed: {
    resolvedBasePartId() {
      const source = String(this.partId || this.productDetails?.PartID || '').split('?')[0];
      if (!source) return '';
      const segments = source.split('-');
      const baseSegments = source.startsWith('W-') ? 4 : 3;
      return segments.slice(0, baseSegments).join('-');
    },
    shownPartId() {
      // Always use productDetails.PartID for display
      return this.productDetails.PartID
    },
    partIdDesktopStyle() {
      return this.partIdDesktopSizeRem ? { fontSize: `${this.partIdDesktopSizeRem}rem` } : {}
    },
    partIdMobileStyle() {
      return this.partIdMobileSizeRem ? { fontSize: `${this.partIdMobileSizeRem}rem` } : {}
    },
    effectiveOptionCombos() {
      if (this.preloadedOptionCombos) {
        return this.preloadedOptionCombos
      }
      if (this.optionCombos) {
        return this.optionCombos
      }
      return this.productDetails?.OptionCombos || this.productDetails?.optionCombos || null
    },
    effectiveOptionTypes() {
      if (Array.isArray(this.preloadedOptionTypes) && this.preloadedOptionTypes.length > 0) {
        return this.preloadedOptionTypes
      }
      if (Array.isArray(this.optionTypes) && this.optionTypes.length > 0) {
        return this.optionTypes
      }
      const backendTypes = this.productDetails?.OptionTypes || this.productDetails?.optionTypes || []
      return Array.isArray(backendTypes) ? backendTypes : []
    },
    optionCombosList() {
      const combos = this.effectiveOptionCombos
      if (!combos) return []
      if (Array.isArray(combos)) return combos

      const nestedCombos = combos.Combos || combos.combos
      if (Array.isArray(nestedCombos)) return nestedCombos

      return []
    },
    hasResolvedCombosPayload() {
      return this.preloadedOptionCombos !== null ||
        this.optionCombos !== null ||
        this.productDetails?.OptionCombos != null ||
        this.productDetails?.optionCombos != null
    },
    isOptionRenderReady() {
      const hasTypes = this.effectiveOptionTypes.length > 0
      const hasCombos = this.optionCombosList.length > 0
      const hasItems = this.normalizedItems.length > 0
      return hasTypes && hasItems && (hasCombos || this.hasResolvedCombosPayload)
    },
    optionsResolved() {
      return !this.hasProductConfigAttributes || this.optionsLoadComplete
    },
    hasItems() {
      return this.normalizedItems.length > 0
    },
    itemsLength() {
      return this.normalizedItems.length
    },
    normalizedItems() {
      if (Array.isArray(this.productDetails?.Items) && this.productDetails.Items.length) {
        return this.productDetails.Items
      }

      const partOptions = Array.isArray(this.productDetails?.PartOptions)
        ? this.productDetails.PartOptions
        : []

      if (partOptions.length) {
        return partOptions.map((option, index) => ({
          OptionID: String(option.OptionID || option.PartID || index),
          OptionType: String(option.OptionType || option.OptionTypeID || '0'),
          OptionName: option.OptionName || option.PartID || '',
          OptionVisualDescription: option.OptionVisualDescription || option.Description || option.PartID || '',
          OptionImage: (option.OptionImage || option.ImageUrl || '').replace(/^\/+/, ''),
          Has3D: Boolean(option.Has3D || option.VaultAvailable),
          UnitPrice: option.UnitPrice,
          UnitPriceLabel: option.UnitPriceLabel,
          PartID: option.PartID || option.OptionID || ''
        }))
      }

      const comboItems = (this.effectiveOptionCombos && (this.effectiveOptionCombos.Items || this.effectiveOptionCombos.items)) || []
      if (Array.isArray(comboItems) && comboItems.length) {
        return comboItems.map((option, index) => ({
          OptionID: String(option.OptionID || option.PartID || index),
          OptionType: String(option.OptionType || option.OptionTypeID || '0'),
          OptionName: option.OptionName || option.PartID || '',
          OptionVisualDescription: option.OptionVisualDescription || option.Description || option.PartID || '',
          OptionImage: (option.OptionImage || option.ImageUrl || '').replace(/^\/+/, ''),
          Has3D: Boolean(option.Has3D || option.VaultAvailable),
          UnitPrice: option.UnitPrice,
          UnitPriceLabel: option.UnitPriceLabel,
          PartID: option.PartID || option.OptionID || ''
        }))
      }

      return []
    },
    curatedOptionsList () {
      let optionsList = this.partsList.map(part => ({
        PartID: part.partId,
        Quantity: parseInt(this.productQuantity),
        UnitPrice: part.unitPrice
      }))
      return optionsList
    },
    isPriceAvailable() {
      const price = this.productDetails.UnitPrice
      return price !== 0 && price !== null && price !== undefined
    },
    formattedPrice() {
      const price = this.productDetails.UnitPrice
      if (price == null) return ''
      return `$${Number(price).toFixed(2)}`
    },
    zeroDollarMessage() {
      return this.productDetails.ZeroDollarPricingMsg || 'Contact us for pricing'
    },
    contactPrefillMessage() {
      const partId = this.overridePartId || this.productDetails.PartID || ''
      const loadCapacity = this.productDetails.LoadCapacity || ''
      const qty = this.productQuantity || 1
      const selections = (this.curatedOptionsList || []).map(o => `${o.PartID} x${o.Quantity}`).join('; ')
      const lines = [
        `Part ID: ${partId}`,
        `Requesting pricing/details for this configuration.`
      ].filter(Boolean)
      return lines.join('\n')
    },
    contactUrl() {
      // HubSpot forms can prefill fields using query params that match field names.
      // Use a generic 'message' param; adjust mapping in HubSpot if needed.
      const base = 'https://www.hamiltoncaster.com/Contact'
      const params = new URLSearchParams({
        message: this.contactPrefillMessage,
        source: 'product-details',
        partId: this.overridePartId || this.productDetails.PartID || ''
      })
      return `${base}?${params.toString()}`
    },
    canAddToProject () {
      // Only block add if the part/options are incompatible
      return !this.isIncompatibleSelection;
    },
      // Track incompatibility state from PartOptionsNew
      isIncompatibleSelection: false,
    shoppingCart () {
      let price = this.overridePrice !== null ? this.overridePrice : this.productDetails.UnitPrice;
      // If price is 0, undefined, or null, send null to the cart
      if (price === 0 || price === undefined || price === null) {
        price = null;
      }
      return {
        CartID: -1,
        CartLineInfo: [
          {
            PartID: this.shownPartId,
            Quantity: parseInt(this.productQuantity),
            UnitPrice: price,
            PartOptions: [
              ...this.curatedOptionsList
            ]
          }
        ]
      };
    }
  },
  watch: {
  optionsResolved(val) {
    if (val) this.optionsEverResolved = true
  },

  resolvedBasePartId(newVal, oldVal) {
    if (oldVal && newVal !== oldVal) {
      this.optionsEverResolved = false
    }
  },

  cadUser: {
    handler: function (cadUser) {
      if (cadUser.DownloadFormats !== '' && cadUser.DownloadFormats != null) {
        this.selectedDownloadFormat = cadUser.DownloadFormats
      }
    },
    deep: true
  },

  shownPartId() {
    this.schedulePartIdFit()
  }
},
  methods: {
    schedulePartIdFit() {
      if (this.partIdFitTimer) {
        clearTimeout(this.partIdFitTimer)
      }
      this.partIdFitTimer = setTimeout(() => {
        this.fitPartIdToContainer()
      }, 0)
    },
    onWindowResize() {
      this.schedulePartIdFit()
    },
    initPartIdResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return
      if (this.partIdResizeObserver) {
        this.partIdResizeObserver.disconnect()
      }
      this.partIdResizeObserver = new ResizeObserver(() => {
        this.schedulePartIdFit()
      })

      const desktop = this.$refs.partIdLineDesktop
      const mobile = this.$refs.partIdLineMobile
      if (desktop && desktop.parentElement) this.partIdResizeObserver.observe(desktop.parentElement)
      if (mobile && mobile.parentElement) this.partIdResizeObserver.observe(mobile.parentElement)
    },
    fitLineToContainer(refName, target) {
      const el = this.$refs[refName]
      if (!el) return

      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') return

      const minRem = target === 'desktop' ? 1.0 : 0.9
      const maxRem = target === 'desktop' ? 3.6 : 2.8
      const stateKey = target === 'desktop' ? 'partIdDesktopSizeRem' : 'partIdMobileSizeRem'

      const fits = (sizeRem) => {
        el.style.fontSize = `${sizeRem}rem`
        return el.scrollWidth <= (el.clientWidth + 1)
      }

      // Reset high first for widest possible readable size.
      if (fits(maxRem)) {
        this[stateKey] = Number(maxRem.toFixed(3))
        return
      }

      if (!fits(minRem)) {
        this[stateKey] = Number(minRem.toFixed(3))
        return
      }

      let low = minRem
      let high = maxRem
      for (let i = 0; i < 14; i++) {
        const mid = (low + high) / 2
        if (fits(mid)) {
          low = mid
        } else {
          high = mid
        }
      }

      this[stateKey] = Number(low.toFixed(3))
    },
   
fitPartIdToContainer() {
  const __layoutStart = performance.now();

  this.$nextTick(() => {
    this.fitLineToContainer('partIdLineDesktop', 'desktop');
    this.fitLineToContainer('partIdLineMobile', 'mobile');

    // ...removed TIMING log...
  });
},

    onPartIdChanged(data) {
      // Always emit the selected partId (optioned or base) to parent for backend fetch
      const selectedPartId = data && typeof data === 'object' ? (data.partId || null) : (data || null)
      console.log('[ProductHeader] onPartIdChanged called with:', data);
      
if (selectedPartId) {
  this.$emit('part-id-updated', selectedPartId);
}

      this.schedulePartIdFit()
    },
    onModifyPreferences () {
      this.$emit('display-cad-modal', true)
    },
    
    addToProject () {
      let svc = this.$parent.moduleService[`svc-${this.$parent.moduleId}`]
      let antiForgeryList = document.getElementsByName("__RequestVerificationToken")
      let antiForgery = antiForgeryList[0].value

      // Session is the source of truth; in-memory cartId can be stale after a bfcache back-navigation.
      const cartFromSession = this.getCartFromSession() || this.cartId
      const payload = { ...this.shoppingCart, previousCartID: cartFromSession || "" }
      if (cartFromSession) {
        this.cartId = cartFromSession
        payload.cartID = cartFromSession
      }

      addToCart(payload, svc, antiForgery)
        .then(res => {
          if (res && res.cartID) {
            this.addCartToSession(res.cartID)
            this.cartId = res.cartID
          }
          console.log('addToCart :: res', res)
          window.location.href = res.redirectLink
        })
    },
    onSearchParts () {
      this.setSessionStorage(ProductTypes.getKey(ProductTypes.Casters), this.searchAttributes)
    },
    setSessionStorage (ConfiguratorType, selectedAttributes) {
      sessionStorage.setItem(ScrollToResults, JSON.stringify(true))
      sessionStorage.setItem('ConfiguratorType', JSON.stringify(ConfiguratorType))
      if (selectedAttributes != null) {
        let key = `SelectedAttributes_${ConfiguratorType}`
        if (selectedAttributes.length > 0) {
          sessionStorage.setItem(key, JSON.stringify(selectedAttributes))
        } else {
          sessionStorage.setItem(key, JSON.stringify([]))
        }
      }
      this.goToResultsPage()
    },
    goToResultsPage () {
      window.location.href = `${location.origin}/${ConfiguratorResultsPage}`
    },
    getCartFromSession() {
      return sessionStorage.getItem('cartId')
    },
    addCartToSession(cartId) {
      sessionStorage.setItem('cartId', cartId)
    },
    
  },
  created () {
  },
  async mounted () {
    
// ...removed TIMING log...

    window.addEventListener('resize', this.onWindowResize)
    this.$nextTick(() => {
      this.initPartIdResizeObserver()
      this.schedulePartIdFit()
    })

    // Use preloaded options when available to avoid post-render shift.
    if (this.preloadedOptionCombos || (this.preloadedOptionTypes && this.preloadedOptionTypes.length)) {
      const combos = this.preloadedOptionCombos;
      const types = this.preloadedOptionTypes || [];
      this.optionCombos = combos;
      this.optionCombosLoaded = !!combos;
      const comboItems = (combos && (combos.Items || combos.items)) || [];
      if (comboItems && comboItems.length) {
        this.$set(this.productDetails, 'Items', comboItems);
      }
      this.optionTypes = types;
      this.optionTypesLoaded = Array.isArray(types);
      return;
    }

    // If backend already supplies options metadata, skip legacy option endpoints.
    const backendCombos = this.productDetails?.OptionCombos || this.productDetails?.optionCombos || null;
    const backendTypes = this.productDetails?.OptionTypes || this.productDetails?.optionTypes || [];
    if (backendCombos && Array.isArray(backendTypes) && backendTypes.length > 0 && this.normalizedItems.length > 0) {
      this.optionCombos = backendCombos;
      this.optionCombosLoaded = true;
      this.optionTypes = backendTypes;
      this.optionTypesLoaded = true;
      return;
    }

    // No header-level fallback fetch. ProductDetails owns option API calls and preloads data.
    this.optionCombos = null;
    this.optionCombosLoaded = false;
    this.optionTypes = [];
    this.optionTypesLoaded = false;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
    if (this.partIdResizeObserver) {
      this.partIdResizeObserver.disconnect()
      this.partIdResizeObserver = null
    }
    if (this.partIdFitTimer) {
      clearTimeout(this.partIdFitTimer)
      this.partIdFitTimer = null
    }
  }
}
</script>

<style lang="scss">
  @import "../../assets/variables";

  .parts-details-header {

    @keyframes optionsFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }

    .options-resolved-block {
      animation: optionsFadeIn 0.15s ease;
    }

    section.container-fluid.part-options-section {
      padding-left: 0px;
    }

    h1 {
      small {
        display: block;
        font-size: 1.5rem;
      }

      .part-id-line {
        display: block;
        max-width: 100%;
        white-space: nowrap;
        line-height: 1.05;
        font-size: clamp(2rem, 5.1vw, 3.6rem);
        overflow: hidden;
      }

      &.mobile-title {
        margin-top: 1rem;

        .part-id-line {
          font-size: clamp(1.5rem, 6.2vw, 2.8rem);
        }
      }

      &.hide-mobile {
        padding-right: 1.25rem;

        .part-id-line {
          max-width: calc(100% - 1.25rem);
        }
      }
    }

    @media screen and (max-width: $large) {
      h1.hide-mobile {
        padding-right: 0;

        .part-id-line {
          max-width: 100%;
          font-size: clamp(1.65rem, 4.6vw, 3rem);
        }
      }
    }

    @media screen and (max-width: $medium) {
      h1.mobile-title {
        .part-id-line {
          font-size: clamp(1.4rem, 5.9vw, 2.6rem);
        }
      }

      section.container-fluid.part-options-section {
        &.part-options-section--reserved {
          min-height: 13.5rem;
        }
      }
    }

    input#downloadProfileName {
    margin-bottom: 0px;
    }
    .sub-head {
      font-size: 21px;
      font-family: 'roboto', sans-serif;
      color: #6C6D6F;
      font-weight: 500;
      display: block;
      line-height: 1.5rem;

      small,
      .small {
        font-weight: normal;
        display: inline-block;
      }
      a {
        color: $primaryColor;
        text-decoration: underline;
      }
    }
    .sub-head + .sub-head {
      margin-top: .35rem;
    }
    .product-description {
      margin: 1rem 0 .8rem;
      line-height: 1.4rem; 
      @media screen and (min-width: $medium) {
         max-width: 44rem;
      }
    }
    .product-summary {

      .download-cad {
        margin: .5rem 0 -1rem;
        align-items: center;
      }

      .purchase-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: .5rem 1rem;
        margin: 3rem 0 1.5rem;
        .button-wrap {
        padding-top: 12px;
        }
        input#downloadProfileName {
        width: 3.5rem;
        text-align: right;
        }
      }

      .price {
        margin: 0; // inside purchase row
        display: inline-flex;
        align-items: end;
        gap: .5rem;
        .price-label {
          font-size: 1rem;
        }
        .price-value {
          font-size: 1rem;
        }
        .caption-wrap {
          display: inline-flex;
          align-items: center;
          gap: .35rem;

          .price-caption {
            margin: 0.6rem .5rem 0 0;
            font-size: 1rem;
            display: inline-block;
            color: $primaryColor;
            font-style: italic;
            font-weight: 700;
            border-bottom: 1px dotted $primaryColor;
            @media screen and (min-width: $medium) {
              font-size: 1rem;
            }
          }

          .pricing-info {
            display: inline-block;
            border-bottom: 1px dotted $primaryColor;
            color: $primaryColor;
          }
        }
      }

      .project {
        margin-top: .3rem;
      }
      .project-inline {
        display: inline-flex;
        align-items: center;
        gap: .5rem;
        .md-field {
          width: 3.5rem;
          margin: 0;
        }
        .button-wrap { 
          display: inline-flex; 
          align-items: center;
          
          .md-button {
            transition: background-color 0.3s ease, opacity 0.3s ease;
            
            &:disabled {
              background-color: #999 !important;
              color: #fff !important;
              opacity: 0.6;
              cursor: not-allowed;
            }
          }
        }
      }
      .download-cad,
      .project {
        input, textarea {
          margin-bottom: 0;
          text-align: right;
          font-size: 1.25rem;
          width: 100%;
        }
        .button-wrap {
          display: flex;
          align-items: center;
        }

      }
    }
    .product-search-button {
      &.md-button {
        justify-self: flex-end;
        margin: 5px 0px 0px 0px;
        height: 36px;
        padding: 0rem .625rem;
        @media screen and (min-width: $large) {
          margin: 5px 0px 0px 0px;
        }

        @media screen and (min-width: $x-large) {
          padding: 0rem 1.625rem;
        }

        .md-button-content {
          font-size: 1rem;
        }
      }
      &.disabled {
        opacity: .7;
        cursor: default;
      }
    }
  }
</style>
